import type { Dream } from '@/store/types/dream.types'
import type { DreamityGoalResDto } from '@/store/types/goal.types'
import type { DreamityPlanResDto } from '@/store/types/plan.types'
import type { DreamityItemResDto } from '@/store/types/checklist.types'
import type { DreamityActionResDto } from '@/store/types/calendar.types'

/** 현재 로컬·동기화 스키마 (v1은 마이그레이션만) */
export const DREAMITY_SNAPSHOT_SCHEMA_VERSION = 2 as const

export interface DreamitySnapshotMeta {
  schemaVersion: 1 | typeof DREAMITY_SNAPSHOT_SCHEMA_VERSION
  dirty: boolean
  localBundleUpdatedAt?: string
  lastKnownServerUpdatedAt?: string
}

export interface DreamitySnapshotDomains {
  dreams?: Dream[]
  goals?: DreamityGoalResDto[]
  plans?: DreamityPlanResDto[]
  items?: DreamityItemResDto[]
  actions?: DreamityActionResDto[]
}

export interface DreamitySnapshot {
  meta: DreamitySnapshotMeta
  domains: DreamitySnapshotDomains
}

function toNumericVersion(sv: unknown): number | null {
  if (sv === 1 || sv === 2) return sv
  if (sv === '1') return 1
  if (sv === '2') return 2
  return null
}

function hasValidSnapshotShape(o: Record<string, unknown>): boolean {
  if (!o.meta || typeof o.meta !== 'object') return false
  const m = o.meta as Record<string, unknown>
  // schemaVersion 은 숫자(1|2) 또는 문자열("1"|"2") 모두 허용
  const sv = toNumericVersion(m.schemaVersion)
  if (sv !== 1 && sv !== DREAMITY_SNAPSHOT_SCHEMA_VERSION) return false
  return typeof m.dirty === 'boolean'
}

export function isDreamitySnapshot(raw: unknown): raw is DreamitySnapshot {
  if (!raw || typeof raw !== 'object') return false
  const o = raw as Record<string, unknown>
  if (!hasValidSnapshotShape(o)) return false
  if (!o.domains || typeof o.domains !== 'object') return false
  return true
}

/**
 * v1 스냅샷 → v2 업그레이드.
 *
 * 두 가지 v1 케이스를 처리합니다.
 *  - 구버전(앱 자체 생성): dreams 배열만 포함, goals/plans/items/actions 없음
 *  - 서버 마이그레이션 v1: schemaVersion:"1"(문자열)이지만 전체 도메인 포함
 *    → 이 경우 도메인 데이터를 그대로 보존해야 함
 */
export function migrateSnapshotToV2(raw: DreamitySnapshot): DreamitySnapshot {
  if (raw.meta.schemaVersion === DREAMITY_SNAPSHOT_SCHEMA_VERSION) {
    return raw
  }

  const hasFullDomains =
    (raw.domains.goals?.length ?? 0) > 0 ||
    (raw.domains.plans?.length ?? 0) > 0 ||
    (raw.domains.items?.length ?? 0) > 0 ||
    (raw.domains.actions?.length ?? 0) > 0

  if (hasFullDomains) {
    // 서버 마이그레이션으로 생성된 v1: 전체 도메인 보존, 버전만 올림
    return {
      meta: {
        schemaVersion: DREAMITY_SNAPSHOT_SCHEMA_VERSION,
        dirty: false,
        localBundleUpdatedAt: raw.meta.localBundleUpdatedAt ?? new Date().toISOString(),
        lastKnownServerUpdatedAt: raw.meta.lastKnownServerUpdatedAt,
      },
      domains: raw.domains,
    }
  }

  // 구버전 앱 생성 v1: dreams만 있고 나머지는 빈 배열로 초기화
  const dreamsIn = (raw.domains.dreams ?? []) as unknown[]
  const dreams: Dream[] = dreamsIn.map((row) => {
    const d = row as Partial<Dream> & { dreamId?: string | number }
    const id =
      typeof d.dreamId === 'string' ? d.dreamId : `legacy_${String(d.dreamId ?? '')}`
    return {
      dreamName: d.dreamName ?? '',
      dreamDesc: d.dreamDesc ?? '',
      startDate: d.startDate ?? '',
      endDate: d.endDate ?? '',
      budget: typeof d.budget === 'number' ? d.budget : 0,
      categoryNames: d.categoryNames ?? null,
      completionRate: d.completionRate ?? null,
      dreamId: id,
      isShared: false,
    }
  })
  return {
    meta: {
      schemaVersion: DREAMITY_SNAPSHOT_SCHEMA_VERSION,
      dirty: true,
      localBundleUpdatedAt: new Date().toISOString(),
      lastKnownServerUpdatedAt: raw.meta.lastKnownServerUpdatedAt,
    },
    domains: {
      dreams,
      goals: [],
      plans: [],
      items: [],
      actions: [],
    },
  }
}

export function normalizePlannerSnapshot(raw: unknown): DreamitySnapshot | null {
  if (!isDreamitySnapshot(raw)) return null
  // schemaVersion 이 숫자 또는 문자열 "1" 모두 v1으로 취급
  const sv = toNumericVersion(raw.meta.schemaVersion)
  if (sv === 1) return migrateSnapshotToV2(raw)
  return raw
}

